import React, { useState, type ComponentType } from 'react';
import uniqueId from 'lodash/uniqueId';
import { v4 as uuid } from 'uuid';
import type { MessageDescriptor } from '@atlassian/jira-intl';
import { NEW_FIELD_OPTION_PREFIX } from '@atlassian/jira-project-settings-apps-common/src/consts.tsx';
import { toOptionUuid } from '@atlassian/jira-shared-types/src/general.tsx';
import type { AssociatedField, OptionType } from '../../../../types/layout-items.tsx';
import type { Limits, ValidationError } from '../../../../types/validation.tsx';
import type { DefaultValueControlComponentProps } from './option/view.tsx';
import { RootOptionsList } from './options-list/index.tsx';
import type { ParentId } from './options-list/view.tsx';
import { canAddNewOptions, groupCascadingSelectFieldOptions } from './util.tsx';

export type Props = {
	readonly label: MessageDescriptor;
	readonly field: AssociatedField;
	readonly fieldErrors: ReadonlyArray<ValidationError>;
	readonly DefaultValueControlLeft?: ComponentType<DefaultValueControlComponentProps>;
	readonly DefaultValueControlRight?: ComponentType<DefaultValueControlComponentProps>;
	readonly onUpdate: (field: AssociatedField) => void;
	readonly limits: Limits;
};

export const CascadingFieldOptions = (props: Props) => {
	const [lastAddedOptimisticId, setLastAddedOptimisticId] = useState<string | null>(null);

	const optionsModified = (options: OptionType[]) => {
		const { field, onUpdate } = props;
		onUpdate({
			...field,
			options,
		});
	};

	const onEditOption = (option: OptionType, newValue: string) => {
		const {
			field: { options },
		} = props;
		if (options == null) {
			return;
		}
		const newOptions: OptionType[] = options.map((o) => {
			if (o === option) {
				return { ...o, value: newValue };
			}
			return o;
		});
		optionsModified(newOptions);
	};

	const onRemoveOption = (option: OptionType) => {
		const {
			field: { options },
		} = props;
		if (options == null || options.length === 1) {
			// There must be at least one option so do not allow deleting the last one
			return;
		}

		optionsModified(
			options.filter((o) => o !== option && o.parentExternalUuid !== option.externalUuid),
		);
	};

	const onAddOption = (parentId: ParentId) => {
		const {
			field,
			field: { options = [] },
			limits,
		} = props;
		// Prevent adding a new option when the add button is disabled and the enter key is pressed
		if (!canAddNewOptions(field, limits)) {
			return;
		}

		const parent =
			parentId === 'root' ? null : options.find((option) => option.externalUuid === parentId);
		if (parent === undefined) {
			return;
		}

		const peerOptions = groupCascadingSelectFieldOptions(options)[parentId] || [];
		const last = peerOptions[peerOptions.length - 1];
		// Do not add another empty option if there is one already
		if (last && last.value === '') {
			return;
		}

		const optionExternalUuid = toOptionUuid(uuid());
		const optimisticId = uniqueId(NEW_FIELD_OPTION_PREFIX);
		const newOptions = [
			...options,
			// Prefix optimisticId to avoid collision with options returned from the API
			{
				value: '',
				id: null,
				optimisticId,
				externalUuid: optionExternalUuid,
				parentId: parent ? parent.id : undefined,
				parentExternalUuid: parent ? parent.externalUuid : undefined,
			},
		];

		setLastAddedOptimisticId(optimisticId);
		optionsModified(newOptions);
	};

	return (
		<RootOptionsList
			{...props}
			onAddOption={onAddOption}
			onEditOption={onEditOption}
			onRemoveOption={onRemoveOption}
			lastAddedOptimisticId={lastAddedOptimisticId}
			optionsByParent={groupCascadingSelectFieldOptions(props.field.options)}
		/>
	);
};
